import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CoursesWrapper = styled.div`
  margin: 4rem 0;
`;

const CourseCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2.5rem;
  margin-bottom: 2.5rem;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const CourseImageContainer = styled.div`
  width: 100%;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    width: 30%;
    margin-bottom: 0;
    margin-right: 2.5rem;
  }
`;

const CourseImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  max-height: 200px;
`;

const CourseContent = styled.div`
  flex: 1;
`;

const CourseTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #004744;
  font-style: italic;
`;

const CourseDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CourseDetail = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #e0e0e0;
  
  svg {
    margin-right: 0.5rem;
    color: #004744;
  }
`;

const CourseDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  color: #e0e0e0;
`;

const CourseTopics = styled.div`
  margin-top: 1.5rem;
`;

const TopicsTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: #004744;
`;

const TopicsList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  grid-gap: 0.5rem 1.5rem;
`;

const TopicItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #e0e0e0;
  
  &::before {
    content: '•';
    color: #004744;
    font-weight: bold;
    margin-right: 0.8rem;
  }
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const CourseButton = styled(Button)`
  margin-top: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #004744;
  font-style: italic;
`;

const TestimonialsContainer = styled.div`
  margin: 4rem 0;
`;

const TestimonialCard = styled.div`
  background-color: rgba(0, 71, 68, 0.1);
  border: 1px solid #004744;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 0.5rem;
    left: 1rem;
    font-size: 5rem;
    font-style: italic;
    color: rgba(0, 71, 68, 0.2);
    line-height: 1;
  }
`;

const TestimonialText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  color: #e0e0e0;
  font-style: italic;
  position: relative;
  z-index: 2;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorAvatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 1rem;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.h4`
  font-size: 1.1rem;
  color: #004744;
  margin-bottom: 0.3rem;
`;

const AuthorRole = styled.p`
  font-size: 0.9rem;
  color: #e0e0e0;
`;

const Courses: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const courses = [
    {
      image: 'https://images.unsplash.com/photo-1579389083046-e3df9c2b3325?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'FONDAMENTI DI GESTIONE DEGLI AFFITTI BREVI' : 'SHORT-TERM RENTAL MANAGEMENT FUNDAMENTALS',
      duration: language === 'it' ? '6 settimane' : '6 weeks',
      level: language === 'it' ? 'Principiante' : 'Beginner',
      format: language === 'it' ? 'Online + Workshop dal vivo' : 'Online + Live Workshop',
      description: language === 'it' 
        ? 'Un corso completo per chi si avvicina al mondo degli affitti brevi. Imparerai le basi della gestione delle proprietà, le strategie di marketing e come massimizzare il rendimento dei tuoi immobili.'
        : 'A comprehensive course for those approaching the world of short-term rentals. You will learn the basics of property management, marketing strategies and how to maximize the yield of your properties.',
      topics: [
        language === 'it' ? 'Panoramica del mercato degli affitti brevi' : 'Overview of the short-term rental market',
        language === 'it' ? 'Aspetti legali e normativi' : 'Legal and regulatory aspects',
        language === 'it' ? 'Preparazione della proprietà' : 'Property preparation',
        language === 'it' ? 'Fotografia e presentazione' : 'Photography and presentation',
        language === 'it' ? 'Pricing e revenue management' : 'Pricing and revenue management',
        language === 'it' ? 'Gestione delle prenotazioni' : 'Booking management',
        language === 'it' ? 'Operazioni giornaliere' : 'Daily operations',
        language === 'it' ? 'Soddisfazione degli ospiti' : 'Guest satisfaction'
      ]
    },
    {
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'MARKETING DIGITALE PER AFFITTI BREVI' : 'DIGITAL MARKETING FOR SHORT-TERM RENTALS',
      duration: language === 'it' ? '4 settimane' : '4 weeks',
      level: language === 'it' ? 'Intermedio' : 'Intermediate',
      format: language === 'it' ? 'Online' : 'Online',
      description: language === 'it' 
        ? 'Impara a promuovere efficacemente le tue proprietà attraverso i canali digitali. Dal posizionamento sulle piattaforme alla creazione di un brand personale, scopri come distinguerti in un mercato competitivo.'
        : 'Learn how to effectively promote your properties through digital channels. From positioning on platforms to creating a personal brand, discover how to stand out in a competitive market.',
      topics: [
        language === 'it' ? 'Ottimizzazione dei profili sulle piattaforme' : 'Platform profile optimization',
        language === 'it' ? 'Strategie SEO per siti web di proprietà' : 'SEO strategies for property websites',
        language === 'it' ? 'Social media marketing' : 'Social media marketing',
        language === 'it' ? 'Email marketing e fidelizzazione' : 'Email marketing and loyalty',
        language === 'it' ? 'Pubblicità a pagamento (PPC)' : 'Pay-per-click advertising (PPC)',
        language === 'it' ? 'Analisi dei dati e KPI' : 'Data analysis and KPIs',
        language === 'it' ? 'Branding personale' : 'Personal branding',
        language === 'it' ? 'Gestione della reputazione online' : 'Online reputation management'
      ]
    },
    {
      image: 'https://images.unsplash.com/photo-1593642533144-3d62aa4783ec?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: language === 'it' ? 'STRATEGIE AVANZATE DI REVENUE MANAGEMENT' : 'ADVANCED REVENUE MANAGEMENT STRATEGIES',
      duration: language === 'it' ? '5 settimane' : '5 weeks',
      level: language === 'it' ? 'Avanzato' : 'Advanced',
      format: language === 'it' ? 'Online + Consulenza individuale' : 'Online + Individual Consulting',
      description: language === 'it' 
        ? 'Un corso di livello avanzato dedicato a host e property manager esperti che vogliono portare la loro attività al livello successivo. Focus su strategie sofisticate di pricing, analisi dei dati e ottimizzazione del rendimento.'
        : 'An advanced level course dedicated to experienced hosts and property managers who want to take their business to the next level. Focus on sophisticated pricing strategies, data analysis and yield optimization.',
      topics: [
        language === 'it' ? 'Modelli di pricing dinamico' : 'Dynamic pricing models',
        language === 'it' ? 'Analisi della stagionalità e dei trend' : 'Analysis of seasonality and trends',
        language === 'it' ? 'Gestione del tasso di occupazione' : 'Occupancy rate management',
        language === 'it' ? 'Strategie per periodi di bassa stagione' : 'Strategies for low season periods',
        language === 'it' ? 'Previsioni di mercato' : 'Market forecasts',
        language === 'it' ? 'Ottimizzazione dei canali di distribuzione' : 'Optimization of distribution channels',
        language === 'it' ? 'Strumenti software e tecnologie' : 'Software tools and technologies',
        language === 'it' ? 'Implementazione di strategie avanzate' : 'Implementation of advanced strategies'
      ]
    }
  ];
  
  const testimonials = [
    {
      text: language === 'it' 
        ? 'Ho partecipato al corso sui fondamenti della gestione degli affitti brevi e posso dire che è stata un\'esperienza trasformativa. In poche settimane sono passato dall\'essere un principiante a sentirmi sicuro nella gestione della mia proprietà. Gli istruttori sono esperti del settore e il materiale è estremamente pratico.'
        : 'I took the course on the fundamentals of short-term rental management and I can say it was a transformative experience. In a few weeks I went from being a beginner to feeling confident in managing my property. The instructors are industry experts and the material is extremely practical.',
      author: {
        name: 'Marco Bianchi',
        role: language === 'it' ? 'Proprietario di casa vacanze' : 'Vacation home owner',
        avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80'
      }
    },
    {
      text: language === 'it' 
        ? 'Il corso sul marketing digitale per affitti brevi mi ha aperto gli occhi su strategie che non avevo mai considerato. Ho implementato quanto appreso e in soli due mesi ho aumentato le prenotazioni del 40%. Consigliatissimo a chi vuole distinguersi in un mercato competitivo.'
        : 'The digital marketing course for short-term rentals opened my eyes to strategies I had never considered. I implemented what I learned and in just two months I increased bookings by 40%. Highly recommended to those who want to stand out in a competitive market.',
      author: {
        name: 'Laura Rossi',
        role: language === 'it' ? 'Property Manager' : 'Property Manager',
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80'
      }
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Corsi | J-O-H' : 'Courses | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Corsi di formazione specializzati per proprietari e gestori di immobili nel settore degli affitti brevi' 
            : 'Specialized training courses for property owners and managers in the short-term rental industry'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/training">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'CORSI' : 'COURSES'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'I nostri corsi specializzati sono progettati per offrirti le competenze e le conoscenze necessarie per eccellere nel mondo degli affitti brevi. Ogni corso è strutturato per fornire apprendimento pratico, con formatori esperti e materiali didattici all\'avanguardia.'
              : 'Our specialized courses are designed to give you the skills and knowledge needed to excel in the world of short-term rentals. Each course is structured to provide practical learning, with expert trainers and cutting-edge teaching materials.'}
          </Description>
          
          <CoursesWrapper>
            {courses.map((course, index) => (
              <CourseCard key={index}>
                <CourseImageContainer>
                  <CourseImage src={course.image} alt={course.title} />
                </CourseImageContainer>
                <CourseContent>
                  <CourseTitle>{course.title}</CourseTitle>
                  <CourseDetails>
                    <CourseDetail>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      {course.duration}
                    </CourseDetail>
                    <CourseDetail>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                      {course.level}
                    </CourseDetail>
                    <CourseDetail>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      {course.format}
                    </CourseDetail>
                  </CourseDetails>
                  <CourseDescription>{course.description}</CourseDescription>
                  <CourseTopics>
                    <TopicsTitle>{language === 'it' ? 'Argomenti Trattati:' : 'Topics Covered:'}</TopicsTitle>
                    <TopicsList>
                      {course.topics.map((topic, idx) => (
                        <TopicItem key={idx}>{topic}</TopicItem>
                      ))}
                    </TopicsList>
                  </CourseTopics>
                  <CourseButton variant="outline">
                    {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
                  </CourseButton>
                </CourseContent>
              </CourseCard>
            ))}
          </CoursesWrapper>
          
          <SectionTitle>
            {language === 'it' ? 'COSA DICONO I NOSTRI STUDENTI' : 'WHAT OUR STUDENTS SAY'}
          </SectionTitle>
          
          <TestimonialsContainer>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <TestimonialText>{testimonial.text}</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar src={testimonial.author.avatar} alt={testimonial.author.name} />
                  <AuthorInfo>
                    <AuthorName>{testimonial.author.name}</AuthorName>
                    <AuthorRole>{testimonial.author.role}</AuthorRole>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsContainer>
          
          <CTASection>
            <Description>
              {language === 'it' 
                ? 'I nostri corsi iniziano regolarmente durante tutto l\'anno. Contattaci per conoscere le prossime date di inizio e per ricevere informazioni dettagliate sui programmi e sui costi.'
                : 'Our courses start regularly throughout the year. Contact us to find out the next start dates and to receive detailed information on programs and costs.'}
            </Description>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci' : 'Contact Us'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default Courses; 